import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Product, CartItem, ProductSize } from '../types';
import { fetchProducts, supabase } from '../services/supabaseService';
import toast from 'react-hot-toast';

interface ShopContextType {
  products: Product[];
  cart: CartItem[];
  isCartOpen: boolean;
  addToCart: (product: Product, size?: ProductSize) => void;
  removeFromCart: (productId: string, sizeName?: string) => void;
  updateCartQuantity: (productId: string, quantity: number, sizeName?: string) => void;
  clearCart: () => void;
  toggleCart: () => void;
  refreshProducts: () => Promise<void>;
  cartTotal: number;
  isLoading: boolean;
}

const ShopContext = createContext<ShopContextType | undefined>(undefined);

export const ShopProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [cart, setCart] = useState<CartItem[]>([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const refreshProducts = async () => {
    try {
      const data = await fetchProducts();
      setProducts(data);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      await refreshProducts();
    };
    loadData();

    // Realtime Subscription
    if (!supabase) return;

    // Polling Fallback (every 60 seconds)
    const intervalId = setInterval(() => {
      refreshProducts();
    }, 60000);

    let channel: ReturnType<typeof supabase.channel> | null = null;

    try {
      channel = supabase
        .channel('public:products')
        .on('postgres_changes', { event: '*', schema: 'public', table: 'products' }, (payload) => {
          console.log('Realtime change received:', payload);
          refreshProducts();
        })
        .subscribe((status, err) => {
          if (status === 'CHANNEL_ERROR') {
            console.warn('Realtime subscription error:', err);
          }
        });
    } catch (e) {
      console.error('Failed to set up realtime subscription:', e);
    }

    return () => {
      if (channel) supabase.removeChannel(channel);
      clearInterval(intervalId);
    };
  }, []);

  const addToCart = (product: Product, size?: ProductSize) => {
    // Determine effective price and stock based on size
    const effectiveStock = size ? size.stock : product.stock;
    const effectivePrice = size ? size.price : product.price;

    // Check current availability vs cart quantity
    // Find item with same ID AND same size (if applicable)
    const existingInCart = cart.find(item =>
      item.id === product.id && item.selectedSize?.name === size?.name
    );
    const cartQuantity = existingInCart ? existingInCart.quantity : 0;

    if (cartQuantity + 1 > effectiveStock) {
      toast.error(`Sorry, only ${effectiveStock} left in stock${size ? ` for size ${size.name}` : ''}`);
      return;
    }

    setCart((prev) => {
      const existing = prev.find((item) =>
        item.id === product.id && item.selectedSize?.name === size?.name
      );

      if (existing) {
        return prev.map((item) =>
          (item.id === product.id && item.selectedSize?.name === size?.name)
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }

      // Add new item with selected size and effective price overridden if needed
      // Note: We keep the original product price structure but the cart item represents the checkout state
      return [...prev, { ...product, quantity: 1, selectedSize: size, price: effectivePrice }];
    });
    setIsCartOpen(true);
  };

  const removeFromCart = (productId: string, sizeName?: string) => {
    setCart((prev) => prev.filter((item) =>
      !(item.id === productId && item.selectedSize?.name === sizeName)
    ));
  };

  const updateCartQuantity = (productId: string, quantity: number, sizeName?: string) => {
    if (quantity < 1) return;

    // Validate stock for the specific item variant
    const itemInCart = cart.find(item => item.id === productId && item.selectedSize?.name === sizeName);
    if (!itemInCart) return;

    // Find original product to check stock properly 
    // (In a real app, you might re-fetch or look up in 'products' state)
    // For now we rely on the item info we have, assuming it has stock info.
    // Ideally, we look up the product in 'products':
    const product = products.find(p => p.id === productId);
    let maxStock = 0;

    if (product) {
      if (sizeName && product.sizes) {
        const sizeVariant = product.sizes.find(s => s.name === sizeName);
        maxStock = sizeVariant ? sizeVariant.stock : 0;
      } else {
        maxStock = product.stock;
      }
    } else {
      // Fallback to what's in cart if product not found in main list (rare)
      maxStock = 99;
    }

    if (quantity > maxStock) {
      toast.error(`Sorry, only ${maxStock} left in stock`);
      return;
    }

    setCart((prev) =>
      prev.map((item) =>
        (item.id === productId && item.selectedSize?.name === sizeName)
          ? { ...item, quantity }
          : item
      )
    );
  };

  const clearCart = () => setCart([]);
  const toggleCart = () => setIsCartOpen(!isCartOpen);

  const cartTotal = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);

  return (
    <ShopContext.Provider
      value={{
        products,
        cart,
        isCartOpen,
        addToCart,
        removeFromCart,
        updateCartQuantity,
        clearCart,
        toggleCart,
        refreshProducts,
        cartTotal,
        isLoading,
      }}
    >
      {children}
    </ShopContext.Provider>
  );
};

export const useShop = () => {
  const context = useContext(ShopContext);
  if (!context) {
    throw new Error('useShop must be used within a ShopProvider');
  }
  return context;
};